import { Autocomplete, Paper, TextField } from '@mui/material';
import { PrimitiveAtom, useAtom } from 'jotai';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useOverlayPortalContainer } from '../../overlay-portal';

interface CellInputProps {
  input: string | undefined;
  onInputChange: (input: string) => void;
  onBlur: () => void;
}

export const CellInput = forwardRef<HTMLInputElement, CellInputProps>(
  function CellInput({ input, onInputChange, onBlur }, ref) {
    return (
      <input
        ref={ref}
        className="h-full min-w-full max-w-0 outline-none"
        autoFocus
        value={input}
        onChange={(e) => {
          onInputChange(e.target.value);
        }}
        onBlur={onBlur}
      />
    );
  },
);

interface CellInputWithAtomProps {
  inputAtom: PrimitiveAtom<string>;
  onBlur: () => void;
}

export const CellInputWithAtom = forwardRef<
  HTMLInputElement,
  CellInputWithAtomProps
>(function CellInputWithAtom({ inputAtom, onBlur }, ref) {
  const [input, setInput] = useAtom(inputAtom);
  return (
    <input
      ref={ref}
      className="h-full min-w-full max-w-0 outline-none"
      autoFocus
      value={input}
      onChange={(e) => {
        setInput(e.target.value);
      }}
      onBlur={onBlur}
    />
  );
});

export const CellInputWithDropdown = forwardRef<
  HTMLInputElement,
  {
    options: { id: string; value: string }[];
    value: string;
    noOptionsText?: string;
    onChange: (value: string) => void;
    onBlur: () => void;
    getOptionDisabled: (option: { id: string; value: string }) => boolean;
  }
>(function CellInputWithDropdown(
  { options, value, noOptionsText, onChange, onBlur, getOptionDisabled },
  ref,
) {
  const { t } = useTranslation();
  const overlayRoot = useOverlayPortalContainer();
  return (
    <Autocomplete
      openOnFocus
      getOptionDisabled={getOptionDisabled}
      noOptionsText={noOptionsText ?? t('noOptions')}
      getOptionLabel={(option) => option.value}
      forcePopupIcon={false}
      disableClearable
      sx={{
        width: '100%',
        height: '100%',

        '& .MuiInputBase-root': {
          height: '100%',
          width: '100%',
          padding: 0,
        },
        '& .MuiInputBase-input': {
          height: '100%',
          padding: '0 4px',
          fontSize: '0.75rem',
        },
      }}
      options={options}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      value={options.find((opt) => opt.id === value) || { id: '', value: '' }}
      onChange={(_, newValue) => {
        onChange(newValue?.id || '');
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={ref}
          variant="standard"
          InputProps={{
            ...params.InputProps,
            disableUnderline: true,
          }}
          autoFocus
          onBlur={onBlur}
        />
      )}
      slotProps={{
        // The sheet grid scrolls, so an in-DOM popper is clipped at the grid
        // edge. Portal onto the enclosing overlay (drawer paper) when the
        // Drawer provided one; otherwise the document body.
        popper: {
          placement: 'bottom-start',
          container: overlayRoot ?? undefined,
          modifiers: [
            { name: 'flip', enabled: true },
            {
              name: 'preventOverflow',
              enabled: true,
              options: { padding: 8 },
            },
          ],
        },
      }}
      PaperComponent={(props) => (
        <Paper
          {...props}
          style={{ width: 'fit-content' }}
          // Prevents useClickOutside from closing the cell (and dropping the
          // click) before Autocomplete's onChange fires, since the popper is
          // portaled outside the sheet grid's DOM.
          onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
        />
      )}
      renderOption={(props, option) => (
        <li
          {...props}
          style={{
            width: '100%',
            whiteSpace: 'nowrap',
          }}
        >
          {option.value}
        </li>
      )}
      ListboxProps={{
        style: { maxHeight: 400 },
      }}
    />
  );
});

export const MetricCellAutocomplete = forwardRef<
  HTMLInputElement,
  {
    options: string[];
    value: string;
    setInput: (value: string) => void;
    onBlur: () => void;
  }
>(function CellInputWithDropdown({ options, value, setInput, onBlur }, ref) {
  return (
    <Autocomplete
      openOnFocus
      options={options}
      forcePopupIcon={false}
      disableClearable
      autoHighlight={false}
      sx={{
        width: '100%',
        height: '100%',
        padding: 0,
        '& .MuiInputBase-root': {
          height: '100%',
          width: '100%',
          padding: 0,
          '& .MuiInput-input': {
            padding: 0,
            paddingLeft: '2px',
            fontSize: '0.75rem',
          },
        },
        '& .MuiInputBase-input': {
          height: '100%',
          padding: 0,
          fontSize: '0.75rem',
        },
      }}
      freeSolo
      filterOptions={(options) => {
        return options;
      }}
      disablePortal
      value={value}
      onInputChange={(_, newValue) => {
        setInput(newValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={ref}
          variant="standard"
          InputProps={{
            ...params.InputProps,
            disableUnderline: true,
          }}
          sx={{
            height: '100%',
          }}
          autoFocus
          onBlur={onBlur}
        />
      )}
      slotProps={{
        popper: {
          placement: 'bottom-start',
          modifiers: [
            {
              name: 'flip',
              enabled: false,
            },
            {
              name: 'preventOverflow',
              enabled: false,
            },
          ],
        },
      }}
      renderOption={(props, option) => {
        return (
          <li
            {...props}
            // prevent the option from being selected when mouse is hovered over dropdown option
            onMouseMove={(e) => {
              e.stopPropagation();
            }}
            onMouseEnter={(e) => {
              e.stopPropagation();
            }}
          >
            {option}
          </li>
        );
      }}
      ListboxProps={{
        style: { maxHeight: 400 },
      }}
    />
  );
});
